import { CHORD_INTERVALS, CHORD_SUFFIX, invert, type ChordQuality } from "./chords";
import { keyScale, keyTonicPc, type MusicalKey } from "./key";
import { pitchClass } from "./notes";

/** Scale degree 1–7. */
export type Degree = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const DEGREES: readonly Degree[] = [1, 2, 3, 4, 5, 6, 7];

/**
 * Simplified functional grouping used in explanations:
 * tonic family (1, 3, 6), predominant (2, 4), dominant (5, 7).
 */
export type HarmonicFunction = "tonic" | "predominant" | "dominant";

export interface DiatonicChord {
  degree: Degree;
  rootPc: number;
  rootName: string; // spelled from the scale
  quality: ChordQuality;
  symbol: string; // e.g. "Em", "F#dim", "Gmaj7"
  roman: string; // e.g. "vi", "vii°", "V7"
  notes: string[]; // spelled chord tones
  pitchClasses: number[];
  function: HarmonicFunction;
}

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII"] as const;

function qualityFromIntervals(iv: number[]): ChordQuality {
  for (const [q, ref] of Object.entries(CHORD_INTERVALS) as [ChordQuality, readonly number[]][]) {
    if (ref.length === iv.length && ref.every((x, i) => x === iv[i])) return q;
  }
  throw new Error(`Unrecognised stacked-thirds chord: ${iv.join(",")}`);
}

function romanFor(degree: Degree, quality: ChordQuality): string {
  const base = ROMAN[degree - 1]!;
  switch (quality) {
    case "maj": return base;
    case "min": return base.toLowerCase();
    case "dim": return `${base.toLowerCase()}°`;
    case "aug": return `${base}+`;
    case "maj7": return `${base}maj7`;
    case "7": return `${base}7`;
    case "m7": return `${base.toLowerCase()}7`;
    case "m7b5": return `${base.toLowerCase()}ø7`;
    case "dim7": return `${base.toLowerCase()}°7`;
    case "mMaj7": return `${base.toLowerCase()}(maj7)`;
    default: return base;
  }
}

export function functionOfDegree(degree: Degree): HarmonicFunction {
  if (degree === 2 || degree === 4) return "predominant";
  if (degree === 5 || degree === 7) return "dominant";
  return "tonic";
}

/** Build the diatonic chord on a degree by stacking scale thirds (triad or seventh). */
export function diatonicChord(key: MusicalKey, degree: Degree, seventh = false): DiatonicChord {
  const scale = keyScale(key);
  const idx = [0, 2, 4, ...(seventh ? [6] : [])].map((o) => (degree - 1 + o) % 7);
  const pcs = idx.map((i) => scale.pitchClasses[i]!);
  const rootPc = pcs[0]!;
  const intervals = pcs.map((pc) => pitchClass(pc - rootPc));
  const quality = qualityFromIntervals(intervals);
  const rootName = scale.notes[idx[0]!]!;
  return {
    degree,
    rootPc,
    rootName,
    quality,
    symbol: rootName + CHORD_SUFFIX[quality],
    roman: romanFor(degree, quality),
    notes: idx.map((i) => scale.notes[i]!),
    pitchClasses: pcs,
    function: functionOfDegree(degree),
  };
}

export function diatonicChords(key: MusicalKey, seventh = false): DiatonicChord[] {
  return DEGREES.map((d) => diatonicChord(key, d, seventh));
}

/**
 * Voice a diatonic chord as MIDI notes near a register.
 * The root is placed in `octave` (scientific pitch: C4 = 60) relative to the key tonic,
 * so degrees ascend from the tonic rather than wrapping at C.
 */
export function voiceDiatonicChord(key: MusicalKey, degree: Degree, opts: { octave?: number; seventh?: boolean; inversion?: number } = {}): number[] {
  const { octave = 3, seventh = false, inversion = 0 } = opts;
  const chord = diatonicChord(key, degree, seventh);
  const tonicMidi = (octave + 1) * 12 + keyTonicPc(key);
  const rootMidi = tonicMidi + pitchClass(chord.rootPc - keyTonicPc(key));
  return invert(CHORD_INTERVALS[chord.quality].map((i) => rootMidi + i), inversion);
}
